/* ═══════════════════════════════════════════════════════════════
   Timesheets rebuild — pure grid math (Tranche 2).
   No React, no fetching — just entries/forecasts/vacation in,
   rows/totals out. Kept separate so the grid component stays a
   renderer and the data hook stays a fetcher.
   ═══════════════════════════════════════════════════════════════ */

import type {
  HourEntry,
  HourForecast,
  VacationRequest,
  FirmHoliday,
  VacationCategory,
  ProjectPhase,
} from '../../../shared/types';
import type { ProjectRow, VacationRow } from './types';

export function getProjectHours(
  entries: HourEntry[],
  projectId: string,
  activityCodeId: string,
  dateISO: string
): number {
  return (
    entries.find(
      (e) =>
        e.projectId === projectId &&
        (e.activityCodeId || '') === activityCodeId &&
        e.date === dateISO &&
        !e.overheadCategory
    )?.hours || 0
  );
}

export function getOverheadHours(entries: HourEntry[], category: string, dateISO: string): number {
  return entries.find((e) => e.overheadCategory === category && e.date === dateISO)?.hours || 0;
}

/** Forecast suggestion for a project-row cell — never a vacation-source
 *  forecast (those render as their own locked section). */
export function getForecastForCell(
  forecasts: HourForecast[],
  projectId: string,
  dateISO: string
): HourForecast | undefined {
  return forecasts.find(
    (f) => f.projectId === projectId && f.dayDate === dateISO && f.sourceKind !== 'vacation'
  );
}

export function buildVacationRows(
  vacationRequests: VacationRequest[],
  firmHolidays: FirmHoliday[],
  vacationCategories: VacationCategory[],
  weekDates: string[]
): VacationRow[] {
  const rows: VacationRow[] = [];

  for (const req of vacationRequests) {
    const catName =
      vacationCategories.find((c) => c.id === req.vacationCategoryId)?.name || 'Vacation';
    const hoursByDate: Record<string, number> = {};
    let total = 0;
    for (const ds of weekDates) {
      const [y, m, d] = ds.split('-').map(Number);
      const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
      if (dow > 0 && dow < 6 && ds >= req.startDate && ds <= req.endDate) {
        hoursByDate[ds] = req.hoursPerDay;
        total += req.hoursPerDay;
      }
    }
    if (total > 0) rows.push({ label: catName, hoursByDate, total });
  }

  for (const h of firmHolidays) {
    const hoursByDate: Record<string, number> = {};
    let total = 0;
    for (const ds of weekDates) {
      if (ds === h.date) {
        hoursByDate[ds] = h.hours;
        total += h.hours;
      }
    }
    if (total > 0) rows.push({ label: `Holiday: ${h.name}`, hoursByDate, total });
  }

  return rows;
}

/** Current phase for a project — priority: in-progress (last) > all
 *  complete (last) > first pending > first phase. Display-only. */
export function getProjectPhase(allPhases: ProjectPhase[], projectId: string): string {
  const projPhases = allPhases
    .filter((ph) => ph.projectId === projectId)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  if (projPhases.length === 0) return '';
  const inProgress = projPhases.filter((ph) => ph.status === 'in-progress');
  if (inProgress.length > 0) return inProgress[inProgress.length - 1].name;
  const complete = projPhases.filter((ph) => ph.status === 'complete');
  if (complete.length === projPhases.length) return projPhases[projPhases.length - 1].name;
  const pending = projPhases.filter((ph) => ph.status === 'pending');
  if (pending.length > 0) return pending[0].name;
  return projPhases[0].name;
}

export function rowProjectTotal(
  entries: HourEntry[],
  row: ProjectRow,
  weekDates: string[]
): number {
  return weekDates.reduce(
    (sum, ds) => sum + getProjectHours(entries, row.projectId, row.activityCodeId, ds),
    0
  );
}

export function overheadCatTotal(
  entries: HourEntry[],
  category: string,
  weekDates: string[]
): number {
  return weekDates.reduce((sum, ds) => sum + getOverheadHours(entries, category, ds), 0);
}

/** True if a project row has at least one empty cell with a forecast
 *  suggestion behind it (i.e. something an "Accept" action could fill). */
export function rowHasUnacceptedGhost(
  entries: HourEntry[],
  forecasts: HourForecast[],
  row: ProjectRow,
  weekDates: string[]
): boolean {
  return weekDates.some((ds) => {
    const hrs = getProjectHours(entries, row.projectId, row.activityCodeId, ds);
    if (hrs > 0) return false;
    const fc = getForecastForCell(forecasts, row.projectId, ds);
    return !!fc && fc.hours > 0;
  });
}
